import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { api, loadMe, useAuth } from '../lib/api';
import { errorMessage } from '../i18n';
import { Banner, Button, Icon } from '../ui';
import { legalUrl } from './LegalLinks';

/** Checkbox "Ich akzeptiere die AGB und habe die Datenschutzerklärung gelesen" (sign-up, onboarding). The server checks it, too. */
export function LegalCheckbox({ checked, onChange, error }: { checked: boolean; onChange: (v: boolean) => void; error?: string | undefined }) {
  return (
    <div className="field">
      <label className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} aria-invalid={error ? true : undefined} style={{ marginTop: 3 }} />
        <span className="small">
          <Trans i18nKey="auth.legalConsent.checkbox" components={{ terms: <a href={legalUrl('/agb')} target="_blank" rel="noreferrer" />, privacy: <a href={legalUrl('/datenschutz')} target="_blank" rel="noreferrer" /> }} />
        </span>
      </label>
      {error ? <span className="err" role="alert">{error}</span> : null}
    </div>
  );
}

/**
 * Banners at the top of the landlord area: confirm the e-mail (new logins, with "Bestätigungsmail erneut senden") and
 * the current AGB/Datenschutz for logins that have not accepted them yet. Neither blocks the app.
 */
export function AccountNotices() {
  const { t } = useTranslation();
  const user = useAuth((s) => s.me?.user);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<{ tone: 'ok' | 'err' | 'info'; text: string } | null>(null);
  if (!user) return null;
  const verify = !!user.emailVerificationRequired;
  const legal = !user.legalConsent?.current;
  if (!verify && !legal) return null;
  const run = async (kind: string, fn: () => Promise<void>) => {
    setBusy(kind);
    setNote(null);
    try {
      await fn();
    } catch (e) {
      setNote({ tone: 'err', text: errorMessage(e) });
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="stack" style={{ gap: 8, marginBottom: 12 }}>
      {verify && (
        <Banner tone="warn" role="status" icon={<Icon name="alert" size={18} strokeWidth={2} />}>
          <div className="stack" style={{ gap: 6 }}>
            <span>{t('auth.verifyEmail.text', { email: user.email })}</span>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              <Button size="sm" variant="secondary" loading={busy === 'send'} onClick={() => void run('send', async () => { const r = await api.auth.resendVerification(); if (r.emailVerified) await loadMe(); setNote({ tone: 'ok', text: r.emailVerified ? t('auth.verifyEmail.done') : t('auth.verifyEmail.sent') }); })}>{t('auth.verifyEmail.resend')}</Button>
              <Button size="sm" variant="ghost" loading={busy === 'check'} onClick={() => void run('check', async () => { const r = await api.auth.refreshVerification(); await loadMe(); setNote(r.user.emailVerificationRequired ? { tone: 'info', text: t('auth.verifyEmail.notYet') } : { tone: 'ok', text: t('auth.verifyEmail.done') }); })}>{t('auth.verifyEmail.check')}</Button>
            </div>
          </div>
        </Banner>
      )}
      {legal && (
        <Banner tone="info" role="status" icon={<Icon name="info" size={18} strokeWidth={2} />}>
          <div className="stack" style={{ gap: 6 }}>
            <span><Trans i18nKey="auth.legalConsent.banner" components={{ terms: <a href={legalUrl('/agb')} target="_blank" rel="noreferrer" />, privacy: <a href={legalUrl('/datenschutz')} target="_blank" rel="noreferrer" /> }} /></span>
            <div className="row"><Button size="sm" variant="secondary" loading={busy === 'legal'} onClick={() => void run('legal', async () => { await api.me.acceptLegal(); await loadMe(); })}>{t('auth.legalConsent.accept')}</Button></div>
          </div>
        </Banner>
      )}
      {note && <Banner tone={note.tone} role="status">{note.text}</Banner>}
    </div>
  );
}
